import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../app/slices/TrainingSlice";
import useSequenceStateOnMap from "./useSequenceStateOnMap";

export interface TakeAttachmentOnMap {
  actorId: string;
  propId: string;
}

/**
 * The take's own attachments (set up in the take's actor settings), for the
 * map when it shows the take rather than a sequence state:
 * - held: a prop in the actor's hand (Prop to Player);
 * - riding: the actor on a placed prop (Player to Prop). The game puts the
 *   actor there when the take starts, so its own spot is only where the take
 *   keeps it.
 * Undefined while the map shows a sequence state (that state has them already).
 */
export default function useTakeAttachmentsOnMap() {
  const focus = useSequenceStateOnMap();
  const take = useSelector(selectSelectedTake3D);

  return useMemo(() => {
    if (focus || !take) return undefined;
    const actorIds = new Set((take.actors ?? []).map((actor) => actor.actorId));
    const placedPropIds = new Set((take.props ?? []).map((prop) => prop.instanceId));
    const held: TakeAttachmentOnMap[] = [];
    const riding: TakeAttachmentOnMap[] = [];
    Object.entries(take.attachments ?? {}).forEach(([propId, attachment]) => {
      if (!actorIds.has(attachment.id)) return;
      if (!attachment.playerToProp) held.push({ actorId: attachment.id, propId });
      else if (placedPropIds.has(propId)) riding.push({ actorId: attachment.id, propId });
    });
    return {
      held,
      riding,
      holderIds: new Set(held.map(({ actorId }) => actorId)),
      riderIds: new Set(riding.map(({ actorId }) => actorId)),
      riddenPropIds: new Set(riding.map(({ propId }) => propId)),
    };
  }, [focus, take]);
}
